<template>
  <v-card class="progress-chart-widget" elevation="0">

    <!-- Header -->
    <div class="widget-header">
      <div class="d-flex align-center" style="gap: 6px;">
        <div class="header-icon-wrap">
          <v-icon x-small color="white">mdi-chart-line</v-icon>
        </div>
        <span class="header-title">Control de Avance</span>
      </div>

      <!-- Series toggles -->
      <div class="series-toggles">
        <v-tooltip bottom v-for="s in seriesOptions" :key="s.key">
          <template v-slot:activator="{ on, attrs }">
            <button
              class="series-btn"
              :class="{ 'series-btn--active': activeSeries.has(s.key) }"
              :style="activeSeries.has(s.key) ? { borderColor: s.color, color: s.color } : {}"
              v-on="on" v-bind="attrs"
              @click="toggleSeries(s.key)"
            >
              <span class="series-dot" :style="{ background: activeSeries.has(s.key) ? s.color : '#D1D5DB' }" />
              {{ s.label }}
            </button>
          </template>
          <span>{{ activeSeries.has(s.key) ? 'Ocultar' : 'Mostrar' }} {{ s.label }}</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Compact notice bar -->
    <div v-if="!showAllPoints" class="widget-notice">
      <div class="notice-left">
        <v-icon x-small color="#D97706" style="margin-right: 4px;">mdi-information-outline</v-icon>
        <span>Datos resumidos por espacio. Amplía el widget o</span>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <button
              class="notice-btn"
              v-on="on" v-bind="attrs"
              @click="showAllPoints = true"
            >
              <v-icon x-small style="margin-right: 2px;">mdi-chart-scatter-plot</v-icon>
              ver todos los puntos
            </button>
          </template>
          <span>Mostrar todos los puntos del gráfico</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Active notice when showAllPoints is on -->
    <div v-else class="widget-notice widget-notice--active">
      <div class="notice-left">
        <v-icon x-small color="#16A34A" style="margin-right: 4px;">mdi-check-circle-outline</v-icon>
        <span>Mostrando todos los puntos</span>
        <button class="notice-btn notice-btn--green" @click="showAllPoints = false">
          <v-icon x-small style="margin-right: 2px;">mdi-arrow-collapse-horizontal</v-icon>
          reducir
        </button>
      </div>
    </div>

    <!-- Chart -->
    <div class="widget-body">
      <div v-if="!selectedProject" class="widget-empty">
        <v-icon size="32" color="grey lighten-2">mdi-chart-line-variant</v-icon>
        <span class="widget-empty-text">Sin proyecto seleccionado</span>
      </div>
      <PlanningLineChart
        v-else
        :show-legend="false"
        :series="activeSeriesArray"
        :show-all-points="showAllPoints"
        class="plc-fill"
      />
    </div>

  </v-card>
</template>

<script>
import PlanningLineChart from '@/components/Charts/PlanningLineChart'

const SERIES_OPTIONS = [
  { key: 'planEc',  label: 'Plan Ec.',  color: '#2D4B95' },
  { key: 'planPh',  label: 'Plan Fís.', color: '#0EA5E9' },
  { key: 'realEc',  label: 'Real Ec.',  color: '#16A34A' },
  { key: 'realPh',  label: 'Real Fís.', color: '#22C55E' },
  { key: 'reprog',  label: 'Reprog.',   color: '#D97706' },
]

export default {
  name: 'ProgressChartWidget',
  components: { PlanningLineChart },

  data() {
    return {
      seriesOptions: SERIES_OPTIONS,
      showAllPoints: false,
      activeSeries: new Set(['planEc', 'realEc', 'reprog']),
    }
  },

  computed: {
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    activeSeriesArray() {
      return [...this.activeSeries]
    },
  },

  methods: {
    toggleSeries(key) {
      const next = new Set(this.activeSeries)
      if (next.has(key)) {
        if (next.size === 1) return  // al menos una serie activa
        next.delete(key)
      } else {
        next.add(key)
      }
      this.activeSeries = next
    },
  },
}
</script>

<style scoped>
.progress-chart-widget {
  border: 1px solid #E5E7EB;
  border-radius: 12px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', 'DM Sans', 'Segoe UI', sans-serif;
}

/* ── Header ── */
.widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 14px;
  background: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
  flex-shrink: 0;
}

.header-icon-wrap {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #2D4B95;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-title {
  font-size: 12px;
  font-weight: 700;
  color: #2D4B95;
  letter-spacing: 0.2px;
}

/* ── Series toggles ── */
.series-toggles {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.series-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 20px;
  border: 1px solid #E5E7EB;
  background: #fff;
  font-size: 10px;
  font-weight: 600;
  color: #9CA3AF;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  outline: none;
}
.series-btn:hover { background: #F3F4F6; }
.series-btn--active { background: #fff; }

.series-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background 0.15s;
}

/* ── Notice bar ── */
.widget-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 14px;
  background: #FFFBEB;
  border-bottom: 1px solid #FDE68A;
  flex-shrink: 0;
  min-height: 26px;
}

.widget-notice--active {
  background: #F0FDF4;
  border-bottom-color: #BBF7D0;
}

.notice-left {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: #92400E;
  flex-wrap: wrap;
  line-height: 1.3;
}

.widget-notice--active .notice-left {
  color: #166534;
}

.notice-btn {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 20px;
  border: 1px solid #D97706;
  background: #fff;
  font-size: 10px;
  font-weight: 600;
  color: #D97706;
  cursor: pointer;
  outline: none;
  transition: background 0.15s, color 0.15s;
  margin-left: 4px;
  white-space: nowrap;
}
.notice-btn:hover {
  background: #FEF3C7;
}

.notice-btn--green {
  border-color: #16A34A;
  color: #16A34A;
}
.notice-btn--green:hover {
  background: #DCFCE7;
}

/* ── Body ── */
.widget-body {
  flex: 1;
  min-height: 160px;
  padding: 10px 14px 8px;
  display: flex;
  flex-direction: column;
}

.plc-fill {
  flex: 1;
  min-height: 0;
}

/* ── Empty state ── */
.widget-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.widget-empty-text {
  font-size: 11px;
  color: #D1D5DB;
}
</style>